/* eslint-disable i18next/no-literal-string  -- .*/
import {
  type PayoutsOverviewFragment,
  useGetGlobalPayoutsHistoryQuery,
} from '@/affiliate-dashboard/graphql';
import { PageHeading } from '@/components/base/page-heading';
import Section from '@/components/base/section';
import { Skeleton } from '@/components/base/skeleton';
import Chart from 'react-apexcharts';
import invariant from 'tiny-invariant';

const config = {
  xaxis: {
    lines: {
      lines: {
        show: true,
      },
    },
    labels: {
      show: true,
    },
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
  },
  colors: ['#246CF9', '#21ecca'],
  yaxis: {
    labels: {
      show: true,
    },
  },
  grid: {
    clipMarkers: false,
    yaxis: {
      lines: {
        show: true,
      },
    },
    strokeDashArray: 5,
    borderColor: '#282C38',
  },
  stroke: {
    curve: 'straight',
    width: [2, 2],
    dropShadow: {
      enabled: true,
      top: 10,
      left: 10,
      blur: 5,
      opacity: 0.5,
    },
  },
  dataLabels: {
    enabled: false,
  },
  tooltip: {
    theme: 'dark',
  },
  chart: {
    toolbar: {
      show: false,
    },
    animations: {
      enabled: false,
    },
    zoom: {
      enabled: false,
    },
    selection: {
      enabled: false,
    },
    dropShadow: {
      enabled: true,
      enabledOnSeries: undefined,
      top: 0,
      left: 0,
      blur: 2,
      color: ['#246CF9', '#21ecca'],
      opacity: 0.85,
    },
  },
  fill: {
    type: 'gradient',
    gradient: {
      shade: 'dark',
      shadeIntensity: 1,
      opacityFrom: 0.5,
      opacityTo: 0,
      stops: [0, 100],
    },
  },
};

export function StatsChart({
  data,
}: {
  data: (PayoutsOverviewFragment | undefined | null)[];
}): JSX.Element {
  const categories =
    data?.map((item) => {
      return item?.month;
    }) ?? [];

  const amounts = data?.map((item) => item?.amount);

  const series = [
    {
      name: 'Amount',
      data: amounts,
    },
  ];

  return (
    <Section variant="secondary" className="h-full">
      <div className="flex w-full flex-col">
        <PageHeading>Global Payouts</PageHeading>

        <div className="flex h-full w-full flex-col justify-center">
          <div className="min-h-56">
            {/* eslint-disable-next-line -- . */
            /*@ts-expect-error*/}
            <Chart
              options={{
                ...config,
                xaxis: { ...config.xaxis, categories },
              }}
              series={series}
              type="area"
              width="100%"
              height="100%"
            />
          </div>
        </div>
      </div>
    </Section>
  );
}

export function GlobalPayoutsChartWithData(): JSX.Element {
  const { data, loading } = useGetGlobalPayoutsHistoryQuery({
    fetchPolicy: 'cache-and-network',
  });

  if (loading) {
    return <Skeleton className="h-full max-h-32 w-full rounded-xl" />;
  }

  invariant(data?.globalPayoutsHistory, 'Data should be defined');

  return (
    <StatsChart data={[...(data?.globalPayoutsHistory ?? [])].reverse()} />
  );
}
